import { ActionIcon, Group, Text } from '@mantine/core';
import { IconGripVertical, IconTrash } from '@tabler/icons-react';

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Block, BlockTree, ScopeComponent } from '@/types';
import { DividerBlockView } from './blocks/DividerBlockView';
import { HeadingBlockView } from './blocks/HeadingBlockView';
import { ParagraphBlockView } from './blocks/ParagraphBlockView';
import { RawBlockView } from './blocks/RawBlockView';
import { SectionBlockView } from './blocks/SectionBlockView';
import { VariableBlockView } from './blocks/VariableBlockView';
import { VariableOption } from './InlineEditor';
import classes from './TemplateEditor.module.css';

interface BlockCanvasProps {
  tree: BlockTree;
  onChange: (next: BlockTree) => void;
  scopeComponents: ScopeComponent[];
  variables: VariableOption[];
  filterOptions: { name: string; description: string }[];
}

export function BlockCanvas({
  tree,
  onChange,
  scopeComponents,
  variables,
  filterOptions,
}: Readonly<BlockCanvasProps>) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = tree.blocks.findIndex((b) => b.id === active.id);
    const newIndex = tree.blocks.findIndex((b) => b.id === over.id);
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }
    onChange({ ...tree, blocks: arrayMove(tree.blocks, oldIndex, newIndex) });
  };

  const updateBlock = (next: Block) => {
    onChange({
      ...tree,
      blocks: tree.blocks.map((b) => (b.id === next.id ? next : b)),
    });
  };

  const removeBlock = (id: string) => {
    onChange({ ...tree, blocks: tree.blocks.filter((b) => b.id !== id) });
  };

  return (
    <div className={classes.blockCanvas}>
      {tree.blocks.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">
          No blocks yet. Drag from the palette on the left, or click a palette item to append.
        </Text>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={tree.blocks.map((b) => b.id)}
            strategy={verticalListSortingStrategy}
          >
            {tree.blocks.map((block) => (
              <SortableBlock
                key={block.id}
                block={block}
                onChange={updateBlock}
                onRemove={removeBlock}
                scopeComponents={scopeComponents}
                variables={variables}
                filterOptions={filterOptions}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

interface SortableBlockProps {
  block: Block;
  onChange: (next: Block) => void;
  onRemove: (id: string) => void;
  scopeComponents: ScopeComponent[];
  variables: VariableOption[];
  filterOptions: { name: string; description: string }[];
}

function SortableBlock({
  block,
  onChange,
  onRemove,
  scopeComponents,
  variables,
  filterOptions,
}: Readonly<SortableBlockProps>) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className={classes.blockShell}>
      <Group className={classes.blockHeader} justify="space-between" wrap="nowrap">
        <Group gap="xs" wrap="nowrap">
          <ActionIcon
            size="sm"
            variant="subtle"
            color="gray"
            {...attributes}
            {...listeners}
            aria-label="Drag block"
            style={{ cursor: 'grab' }}
          >
            <IconGripVertical size={14} />
          </ActionIcon>
          <Text size="xs" tt="uppercase" fw={600} c="dimmed">
            {labelFor(block)}
          </Text>
        </Group>
        <ActionIcon
          size="sm"
          variant="subtle"
          color="red"
          onClick={() => onRemove(block.id)}
          aria-label="Delete block"
        >
          <IconTrash size={14} />
        </ActionIcon>
      </Group>

      <BlockBody
        block={block}
        onChange={onChange}
        scopeComponents={scopeComponents}
        variables={variables}
        filterOptions={filterOptions}
      />
    </div>
  );
}

type BlockBodyProps = Omit<SortableBlockProps, 'onRemove'>;

function BlockBody({
  block,
  onChange,
  scopeComponents,
  variables,
  filterOptions,
}: Readonly<BlockBodyProps>) {
  switch (block.kind) {
    case 'heading':
      return (
        <HeadingBlockView
          block={block}
          onChange={onChange}
          variables={variables}
          filterOptions={filterOptions}
        />
      );
    case 'paragraph':
      return (
        <ParagraphBlockView
          block={block}
          onChange={onChange}
          variables={variables}
          filterOptions={filterOptions}
        />
      );
    case 'divider':
      return <DividerBlockView />;
    case 'section':
      return (
        <SectionBlockView block={block} onChange={onChange} scopeComponents={scopeComponents} />
      );
    case 'variable':
      return (
        <VariableBlockView
          block={block}
          onChange={onChange}
          variables={variables}
          filterOptions={filterOptions}
        />
      );
    case 'raw':
      return <RawBlockView block={block} />;
  }
}

function labelFor(block: Block): string {
  switch (block.kind) {
    case 'heading':
      return `Heading (H${block.level})`;
    case 'paragraph':
      return 'Paragraph';
    case 'divider':
      return 'Divider';
    case 'section':
      return 'Section';
    case 'variable':
      return 'Variable';
    case 'raw':
      return 'Raw Jinja';
  }
}
